import React, { useState, useEffect, useCallback } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import Sidebar from "./Sidebar";
import TopNavbar from "../top-navbar/TopNav";
import PersonalInfo from "./PersonalInfo";
import AddressInfo from "./AddressInfo";
import EducationInfo from "./EducationInfo";
import EmploymentCheckPhysical from "./employment-check-physical";
import EmploymentCheckVirtual from "./Employment-check-Virtual";
import AddAttachment from "./AddAttachment";
import UanCheck from "./UanCheck";
import CriminalCheck from "./CriminalCheck";
import "../../styles/employee/candidate-details.css";
import { getCandidateInfo } from "../../services/employee/api";

const CandidateDetails = () => {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const token = localStorage.getItem("bgc_token");

  const activeCheck = searchParams.get("check");

  const [statusData, setStatusData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editData, setEditData] = useState(null);
  const [uanCheckStatus, setUanCheckStatus] = useState("WORK_IN_PROGRESS");
  const [criminalCheckStatus, setCriminalCheckStatus] =
    useState("WORK_IN_PROGRESS");
  const [hrContactStatuses, setHrContactStatuses] = useState([]);

  const fetchCandidateStatus = useCallback(async () => {
    if (!id || !token) {
      setError("Missing candidate ID or token");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await getCandidateInfo(id);

      if (response.data?.status && response.data.data) {
        const {
          candidateFullInfoResponse,
          uanCheckStatus,
          criminalcheckStatus,
          hrContactStatuses,
        } = response.data.data;

        setStatusData(candidateFullInfoResponse);
        setUanCheckStatus(uanCheckStatus);
        setCriminalCheckStatus(criminalcheckStatus);
        setHrContactStatuses(hrContactStatuses || []);
      } else {
        setError(response.data?.message || "Failed to fetch status.");
      }
    } catch {
      setError("Failed to fetch candidate status.");
    } finally {
      setLoading(false);
    }
  }, [id, token]);

  useEffect(() => {
    fetchCandidateStatus();
  }, [fetchCandidateStatus]);

  const handleEditCheck = (checkType, data) => {
    setEditData(data);
    setSearchParams({ check: checkType });
  };

  const handleBackFromCheckEdit = () => {
    setEditData(null);
    setSearchParams({});
    fetchCandidateStatus();
  };

  const isCompleted = (status) => status?.toUpperCase() === "COMPLETED";

  const areAllCompleted = (arr) =>
    Array.isArray(arr) &&
    arr.length > 0 &&
    arr.every((item) => isCompleted(item.bgcStatus));

  const getStatusClass = (status) =>
    isCompleted(status) ? "status-completed" : "status-work-in-progress";

  const getDisplayStatus = (status) =>
    isCompleted(status) ? "Completed" : "Work In Progress";

  const LoadingSpinner = () => (
    <div className="centered-message">
      <div className="spinner"></div>
      <div>Loading candidate details...</div>
    </div>
  );

  const ErrorMessage = ({ message }) => (
    <div className="centered-message">
      <div>Error: {message}</div>
    </div>
  );

  const NoDataMessage = () => (
    <div className="centered-message">
      <div>No candidate information available.</div>
    </div>
  );

  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorMessage message={error} />;
  if (!statusData) return <NoDataMessage />;

  const {
    personalInfoResponse,
    addressInfoResponse,
    educationInfos,
    employmentInfos,
  } = statusData;

  const hasUanNumber =
    Array.isArray(employmentInfos) &&
    employmentInfos.some((emp) => emp.uanNumber);

  /* ================= CHILD SCREENS ================= */

  if (activeCheck === "personal")
    return (
      <PersonalInfo
        candidateId={id}
        personalInfoResponse={personalInfoResponse}
        onBack={handleBackFromCheckEdit}
      />
    );

  if (activeCheck === "address")
    return (
      <AddressInfo
        candidateId={id}
        addressInfoResponse={addressInfoResponse}
        onBack={handleBackFromCheckEdit}
      />
    );

  if (activeCheck === "education")
    return (
      <EducationInfo
        candidateId={id}
        educationInfos={educationInfos}
        onBack={handleBackFromCheckEdit}
      />
    );

  if (activeCheck === "employment")
    return (
      <EmploymentCheckPhysical
        candidateId={id}
        employmentInfos={employmentInfos}
        onBack={handleBackFromCheckEdit}
      />
    );

  if (activeCheck === "employment-virtual")
    return (
      <EmploymentCheckVirtual
        candidateId={id}
        candidateName={personalInfoResponse?.firstName}
        candidateEmail={personalInfoResponse?.email}
        employmentInfos={employmentInfos}
        hrContactStatuses={hrContactStatuses}
        onBack={handleBackFromCheckEdit}
      />
    );

  if (activeCheck === "add-attachment")
    return (
      <AddAttachment
        candidateId={id}
        employmentInfos={employmentInfos}
        onBack={handleBackFromCheckEdit}
      />
    );

  if (activeCheck === "uan")
    return <UanCheck candidateId={id} onBack={handleBackFromCheckEdit} />;

  if (activeCheck === "criminalCheck")
    return <CriminalCheck candidateId={id} onBack={handleBackFromCheckEdit} />;

  /* ================= DASHBOARD ================= */

  return (
    <div className="dashboard-app-container">
      <Sidebar />
      <div className="dashboard-right-panel">
        <TopNavbar />
        <h2 className="cadidate-details-header">All Details</h2>

        <div className="candidate-details-container">
          <div className="candidate-checks-grid">

            {/* PERSONAL */}
            {personalInfoResponse && (
              <div className={`check-card ${getStatusClass(personalInfoResponse.bgcStatus)}`}>
                <div className="check-card-content">
                  <div className="check-name">Personal Check</div>
                  <span className="check-status">
                    {getDisplayStatus(personalInfoResponse.bgcStatus)}
                  </span>
                </div>
                {!isCompleted(personalInfoResponse.bgcStatus) && (
                  <div className="check-actions">
                    <button onClick={() => handleEditCheck("personal")}>
                      &#9998;
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* ADDRESS */}
            {addressInfoResponse && (
              <div className={`check-card ${getStatusClass(addressInfoResponse.bgcStatus)}`}>
                <div className="check-card-content">
                  <div className="check-name">Address Check</div>
                  <span className="check-status">
                    {getDisplayStatus(addressInfoResponse.bgcStatus)}
                  </span>
                </div>
                {!isCompleted(addressInfoResponse.bgcStatus) && (
                  <div className="check-actions">
                    <button onClick={() => handleEditCheck("address")}>
                      &#9998;
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* EDUCATION */}
            <div className={`check-card ${
              educationInfos?.length
                ? getStatusClass(
                    areAllCompleted(educationInfos)
                      ? "COMPLETED"
                      : "WORK_IN_PROGRESS"
                  )
                : "status-not-available"
            }`}>
              <div className="check-card-content">
                <div className="check-name">Education Check</div>
                {educationInfos?.length ? (
                  <>
                    <p>Entries: {educationInfos.length}</p>
                    <span className="check-status">
                      {areAllCompleted(educationInfos)
                        ? "Completed"
                        : "Work In Progress"}
                    </span>
                  </>
                ) : (
                  <span className="check-status status-na-text">
                    No education records available
                  </span>
                )}
              </div>
              {educationInfos?.length > 0 &&
                !areAllCompleted(educationInfos) && (
                  <div className="check-actions">
                    <button onClick={() => handleEditCheck("education")}>
                      &#9998;
                    </button>
                  </div>
                )}
            </div>

            {/* EMPLOYMENT PHYSICAL */}
            <div className={`check-card ${
              employmentInfos?.length
                ? getStatusClass(
                    areAllCompleted(employmentInfos)
                      ? "COMPLETED"
                      : "WORK_IN_PROGRESS"
                  )
                : "status-not-available"
            }`}>
              <div className="check-card-content">
                <div className="check-name">Employment Check (Physical)</div>
                {employmentInfos?.length ? (
                  <>
                    <p>Entries: {employmentInfos.length}</p>
                    <span className="check-status">
                      {areAllCompleted(employmentInfos)
                        ? "Completed"
                        : "Work In Progress"}
                    </span>
                  </>
                ) : (
                  <span className="check-status status-na-text">
                    No employment records available
                  </span>
                )}
              </div>
              {employmentInfos?.length > 0 &&
                !areAllCompleted(employmentInfos) && (
                  <div className="check-actions">
                    <button onClick={() => handleEditCheck("employment")}>
                      &#9998;
                    </button>
                  </div>
                )}
            </div>

            {/* EMPLOYMENT VIRTUAL */}
            <div className={`check-card ${
              employmentInfos?.length
                ? getStatusClass(
                    areAllCompleted(employmentInfos)
                      ? "COMPLETED"
                      : "WORK_IN_PROGRESS"
                  )
                : "status-not-available"
            }`}>
              <div className="check-card-content">
                <div className="check-name">Employment Check (Virtual)</div>
                {employmentInfos?.length ? (
                  <>
                    <p>Entries: {employmentInfos.length}</p>
                    <span className="check-status">
                      {areAllCompleted(employmentInfos)
                        ? "Completed"
                        : "Work In Progress"}
                    </span>
                  </>
                ) : (
                  <span className="check-status status-na-text">
                    No employment records available
                  </span>
                )}
              </div>
              {employmentInfos?.length > 0 &&
                !areAllCompleted(employmentInfos) && (
                  <div className="check-actions">
                    <button onClick={() => handleEditCheck("add-attachment")}>
                      &#x2795;
                    </button>
                    <button onClick={() => handleEditCheck("employment-virtual")}>
                      &#9998;
                    </button>
                  </div>
                )}
            </div>

            {/* UAN */}
            <div className={`check-card ${
              hasUanNumber
                ? getStatusClass(uanCheckStatus)
                : "status-not-available"
            }`}>
              <div className="check-card-content">
                <div className="check-name">UAN Check</div>
                {hasUanNumber ? (
                  <span className="check-status">
                    {getDisplayStatus(uanCheckStatus)}
                  </span>
                ) : (
                  <span className="check-status status-na-text">
                    UAN not available
                  </span>
                )}
              </div>
              {hasUanNumber && !isCompleted(uanCheckStatus) && (
                <div className="check-actions">
                  <button onClick={() => handleEditCheck("uan")}>
                    &#9998;
                  </button>
                </div>
              )}
            </div>

            {/* CRIMINAL */}
            <div className={`check-card ${getStatusClass(criminalCheckStatus)}`}>
              <div className="check-card-content">
                <div className="check-name">Criminal Check</div>
                <span className="check-status">
                  {getDisplayStatus(criminalCheckStatus)}
                </span>
              </div>
              {!isCompleted(criminalCheckStatus) && (
                <div className="check-actions">
                  <button onClick={() => handleEditCheck("criminalCheck")}>
                    &#9998;
                  </button>
                </div>
              )}
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default CandidateDetails;